/**
 * Camera capture component with optional face oval overlay.
 *
 * Opens the device camera using `getUserMedia` with mobile-adaptive
 * resolution, renders a live video preview, and provides snapshot capture
 * via a hidden canvas. Supports a face oval overlay for selfie/liveness
 * flows and a children slot for custom overlay content.
 *
 * WCAG 2.2 / EAA compliance:
 * - Keyboard shortcut: Space / Enter to capture when camera is active
 * - All states have descriptive `aria-label` for screen readers
 * - Visible focus indicator on the capture area when focused via keyboard
 * - Camera status communicated via `role="status"` / `role="alert"`
 *
 * @module components/CameraCapture
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Security information about the detected camera device. */
export interface DeviceSecurityInfo {
  /** The label reported by the browser for the active video input device. */
  cameraLabel: string;
  /** True if the camera label matches a known virtual camera pattern. */
  isVirtualCamera: boolean;
  /** Name of the matched virtual camera software, if detected. */
  virtualCameraName?: string;
  /** True if browser automation markers are detected (e.g. navigator.webdriver). */
  isAutomation: boolean;
}

export interface CameraCaptureProps {
  /** Camera direction. Defaults to `'user'` (front-facing). */
  facingMode?: 'user' | 'environment';
  /** Called with a JPEG blob when a snapshot is captured. */
  onCapture: (blob: Blob) => void;
  /** Called when the camera stream is established. */
  onStream?: (stream: MediaStream) => void;
  /** Show a dashed ellipse overlay to guide face positioning. */
  showFaceOval?: boolean;
  /** Overlay content rendered on top of the video feed. */
  children?: React.ReactNode;
  /**
   * Optional external ref to the `<video>` element. When provided the
   * component assigns the video element to both its own internal ref and
   * this external ref so callers (e.g. LivenessChallenge) can read frames.
   */
  videoRef?: React.RefObject<HTMLVideoElement | null>;
  /**
   * Called after the camera stream is established with security information
   * about the detected device. The parent component can decide whether to
   * block or proceed based on virtual camera / automation detection.
   */
  onDeviceInfo?: (info: DeviceSecurityInfo) => void;
}

type CameraStatus = 'idle' | 'requesting' | 'active' | 'error';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const IS_MOBILE = typeof navigator !== 'undefined'
  ? /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  : false;

function getVideoConstraints(
  facingMode: 'user' | 'environment',
): MediaStreamConstraints['video'] {
  if (IS_MOBILE) {
    return {
      facingMode,
      width: { ideal: 480 },
      height: { ideal: 640 },
    };
  }
  return {
    facingMode,
    width: { ideal: 640 },
    height: { ideal: 480 },
  };
}

function getErrorMessage(err: unknown, t: TFunction): string {
  if (err instanceof DOMException) {
    switch (err.name) {
      case 'NotAllowedError':
        return t('cameraCapture.errors.notAllowed');
      case 'NotFoundError':
        return t('cameraCapture.errors.notFound');
      case 'NotReadableError':
        return t('cameraCapture.errors.notReadable');
      case 'OverconstrainedError':
        return t('cameraCapture.errors.overconstrained');
      default:
        return t('cameraCapture.errors.generic', { message: err.message });
    }
  }
  if (err instanceof Error) return err.message;
  return t('cameraCapture.errors.unexpected');
}

/** Get the appropriate aria-label for the current camera status. */
function getCameraAriaLabel(status: CameraStatus, t: TFunction): string {
  switch (status) {
    case 'idle':
    case 'requesting':
      return t('cameraCapture.ariaLabel.requesting');
    case 'active':
      return t('cameraCapture.ariaLabel.active');
    case 'error':
      return t('cameraCapture.ariaLabel.error');
  }
}

// ---------------------------------------------------------------------------
// Virtual camera / automation detection
// ---------------------------------------------------------------------------

/** Known virtual camera software name patterns (lowercase). */
const VIRTUAL_CAMERA_PATTERNS = [
  'obs',
  'manycam',
  'xsplit',
  'snap camera',
  'vcam',
  'epoccam',
  'iriun',
  'droidcam',
] as const;

/** Automation markers to check in user agent (lowercase). */
const AUTOMATION_MARKERS = ['webdriver', 'headless', 'bot', 'selenium'] as const;

/**
 * Detect whether the active video track comes from a virtual camera and
 * whether the browser environment shows signs of automation.
 */
async function detectDeviceSecurity(
  stream: MediaStream,
): Promise<DeviceSecurityInfo> {
  const videoTrack = stream.getVideoTracks()[0];
  let cameraLabel = videoTrack?.label ?? '';

  // If the track label is empty, try enumerating devices to find a match.
  if (!cameraLabel && typeof navigator.mediaDevices?.enumerateDevices === 'function') {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((d) => d.kind === 'videoinput');
      // If only one video device exists, use its label.
      if (videoDevices.length === 1 && videoDevices[0].label) {
        cameraLabel = videoDevices[0].label;
      }
    } catch {
      // enumerateDevices may fail in restricted contexts; proceed with empty label.
    }
  }

  const labelLower = cameraLabel.toLowerCase();

  // Check for known virtual camera patterns.
  let isVirtualCamera = false;
  let virtualCameraName: string | undefined;

  for (const pattern of VIRTUAL_CAMERA_PATTERNS) {
    if (labelLower.includes(pattern)) {
      isVirtualCamera = true;
      virtualCameraName = pattern;
      break;
    }
  }

  // Also flag if the label contains "virtual" as a generic indicator.
  if (!isVirtualCamera && labelLower.includes('virtual')) {
    isVirtualCamera = true;
    virtualCameraName = 'virtual';
  }

  // Check for browser automation markers.
  const isWebdriver = !!(navigator as unknown as Record<string, unknown>).webdriver;
  const uaLower = navigator.userAgent.toLowerCase();
  const isAutomation =
    isWebdriver || AUTOMATION_MARKERS.some((marker) => uaLower.includes(marker));

  return {
    cameraLabel,
    isVirtualCamera,
    virtualCameraName: isVirtualCamera ? virtualCameraName : undefined,
    isAutomation,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CameraCapture({
  facingMode = 'user',
  onCapture,
  onStream,
  showFaceOval = false,
  children,
  videoRef: externalVideoRef,
  onDeviceInfo,
}: CameraCaptureProps) {
  const { t } = useTranslation('portal');
  const internalVideoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const instructionsId = React.useId();

  // Combine internal and optional external video ref via a callback ref.
  const videoRefCallback = useCallback(
    (node: HTMLVideoElement | null) => {
      // Assign to internal ref.
      (internalVideoRef as React.MutableRefObject<HTMLVideoElement | null>).current = node;
      // Assign to external ref if provided.
      if (externalVideoRef) {
        (externalVideoRef as React.MutableRefObject<HTMLVideoElement | null>).current = node;
      }
    },
    [externalVideoRef],
  );

  // Alias for the rest of the component to keep the diff small.
  const videoRef = internalVideoRef;

  const [status, setStatus] = useState<CameraStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // -------------------------------------------------------------------
  // Start camera
  // -------------------------------------------------------------------

  const startCamera = useCallback(async () => {
    setStatus('requesting');
    setErrorMessage(null);

    try {
      const constraints: MediaStreamConstraints = {
        video: getVideoConstraints(facingMode),
        audio: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      setStatus('active');
      onStream?.(stream);

      // Run virtual camera / automation detection asynchronously.
      // This does not block the camera -- we report findings to the parent.
      if (onDeviceInfo) {
        detectDeviceSecurity(stream).then(
          (info) => onDeviceInfo(info),
          () => {
            // Detection failure is non-fatal; report minimal info.
            onDeviceInfo({
              cameraLabel: '',
              isVirtualCamera: false,
              isAutomation: false,
            });
          },
        );
      }
    } catch (err: unknown) {
      setStatus('error');
      setErrorMessage(getErrorMessage(err, t));
    }
  }, [facingMode, onStream, onDeviceInfo, t]);

  // -------------------------------------------------------------------
  // Stop camera
  // -------------------------------------------------------------------

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        track.stop();
      }
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  // -------------------------------------------------------------------
  // Capture snapshot
  // -------------------------------------------------------------------

  const captureSnapshot = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (blob) onCapture(blob);
      },
      'image/jpeg',
      0.85,
    );
  }, [onCapture]);

  // -------------------------------------------------------------------
  // Keyboard handler: Space/Enter to capture when active
  // -------------------------------------------------------------------

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (status !== 'active') return;

      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        captureSnapshot();
      }
    },
    [status, captureSnapshot],
  );

  // -------------------------------------------------------------------
  // Lifecycle: auto-start on mount, cleanup on unmount
  // -------------------------------------------------------------------

  useEffect(() => {
    startCamera();
    return () => stopCamera();
    // We intentionally only run this on mount/unmount. The facingMode change
    // is handled by the dependency on startCamera which captures facingMode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facingMode]);

  // -------------------------------------------------------------------
  // Render: Idle / Requesting
  // -------------------------------------------------------------------

  if (status === 'idle' || status === 'requesting') {
    return (
      <div
        className="flex aspect-[3/4] w-full items-center justify-center rounded-lg bg-ink-surface sm:aspect-[4/3]"
        role="status"
        aria-label={getCameraAriaLabel(status, t)}
      >
        <div className="text-center">
          <div
            className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent"
            aria-hidden="true"
          />
          <p className="text-sm text-ink-muted">
            {t('cameraCapture.requestingAccess')}
          </p>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------
  // Render: Error
  // -------------------------------------------------------------------

  if (status === 'error') {
    return (
      <div
        className="flex aspect-[3/4] w-full flex-col items-center justify-center rounded-lg bg-ink-surface px-6 sm:aspect-[4/3]"
        role="alert"
        aria-label={getCameraAriaLabel(status, t)}
      >
        {/* Camera-off icon */}
        <svg
          className="mb-3 h-10 w-10 text-danger"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M12 18.75H4.5a2.25 2.25 0 01-2.25-2.25V7.5A2.25 2.25 0 014.5 5.25h7.5"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3.75 3.75l16.5 16.5"
          />
        </svg>
        <p className="mb-4 text-center text-sm text-ink-invert">
          {errorMessage}
        </p>
        <button
          type="button"
          onClick={startCamera}
          className="inline-flex h-10 items-center gap-2 rounded-lg bg-white/10 px-5 text-sm font-medium text-white transition-colors hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182"
            />
          </svg>
          {t('cameraCapture.tryAgain')}
        </button>
      </div>
    );
  }

  // -------------------------------------------------------------------
  // Render: Active camera
  // -------------------------------------------------------------------

  return (
    <div
      ref={containerRef}
      className="nv-camera-capture relative w-full overflow-hidden rounded-lg bg-ink-surface"
      tabIndex={0}
      role="region"
      aria-label={getCameraAriaLabel(status, t)}
      aria-describedby={instructionsId}
      onKeyDown={handleKeyDown}
    >
      <p id={instructionsId} className="sr-only">
        {t('cameraCapture.instructions')}
      </p>
      {/* Live video feed */}
      <video
        ref={videoRefCallback}
        autoPlay
        playsInline
        muted
        className="block w-full"
        aria-label={t('cameraCapture.cameraPreview')}
      />

      {/* Hidden canvas for snapshot capture */}
      <canvas ref={canvasRef} className="hidden" aria-hidden="true" />

      {/* Face oval overlay */}
      {showFaceOval && (
        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
          aria-hidden="true"
        >
          <div
            className="h-[65%] w-[55%] rounded-[50%] border-[3px] border-dashed border-white/60"
            style={{
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.35)',
            }}
          />
        </div>
      )}

      {/* Custom overlay content (children) */}
      {children && (
        <div className="absolute inset-0">{children}</div>
      )}

      {/* Capture button */}
      <div className="absolute inset-x-0 bottom-0 flex justify-center pb-5">
        <button
          type="button"
          onClick={captureSnapshot}
          className="nv-capture-button flex h-16 w-16 items-center justify-center rounded-full border-4 border-white bg-white/20 shadow-lg transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white/80 focus:ring-offset-2 focus:ring-offset-ink-surface active:scale-95"
          aria-label={t('cameraCapture.capturePhoto')}
        >
          <div className="h-12 w-12 rounded-full bg-white" />
        </button>
      </div>

      {/* Keyboard hint (visible on focus, hidden otherwise) */}
      <div className="nv-keyboard-hint pointer-events-none absolute inset-x-0 top-3 flex justify-center opacity-0 transition-opacity duration-200">
        <span className="rounded-md bg-black/60 px-3 py-1 text-xs text-white/80 backdrop-blur-sm">
          {t('cameraCapture.keyboardHint')}
        </span>
      </div>
    </div>
  );
}
